import FileUploadIcon from "@mui/icons-material/FileUpload";
import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { Fragment, useState } from "react";
import {
  Button,
  Form,
  ImageInput,
  NumberInput,
  RadioButtonGroupInput,
  required,
  useNotify,
} from "react-admin";
import { FileRejection } from "react-dropzone";
import { FieldValues } from "react-hook-form";
import PreviewImageField from "../../../../forms/PreviewImageField";
import { useUploadManager } from "../../../../uploads/UploadManager";

export type MediaUploadUser = {
  id: string;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
};

type MediaUploadForm = {
  semana: number;
  categoryVideo: string;
  categoryImage: string;
  images: { rawFile: File }[];
};

function getUserLabel(user: MediaUploadUser) {
  const fullName = [user.firstname, user.lastname].filter(Boolean).join(" ");
  return fullName || user.email || `Usuario ${user.id.slice(0, 8)}`;
}

function MediaUploadDialog({ user }: { user: MediaUploadUser }) {
  const [isOpen, setOpen] = useState(false);
  const [formVersion, setFormVersion] = useState(0);
  const notify = useNotify();
  const { enqueue } = useUploadManager();

  const startUpload = (formValues: FieldValues) => {
    const data = formValues as MediaUploadForm;
    const files = (data.images || [])
      .map((file) => file.rawFile)
      .filter((file): file is File => file instanceof File);

    if (files.length === 0) {
      notify("Selecciona al menos una imagen o vídeo", { type: "error" });
      return;
    }

    enqueue({
      userId: user.id,
      userLabel: getUserLabel(user),
      semana: Number(data.semana),
      categoryImage: String(data.categoryImage),
      categoryVideo: String(data.categoryVideo),
      files,
    });
    notify("Subida iniciada. Puedes seguir usando el admin.", {
      type: "info",
    });
    setFormVersion((version) => version + 1);
    setOpen(false);
  };

  return (
    <Fragment>
      <Stack width={270}>
        <Button
          variant="contained"
          label="Subir contenido"
          onClick={() => setOpen(true)}
        >
          <FileUploadIcon />
        </Button>
      </Stack>
      <br />
      <Dialog
        open={isOpen}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <Form
          key={formVersion}
          onSubmit={startUpload}
          defaultValues={{ categoryVideo: "1", categoryImage: "0" }}
        >
          <DialogTitle>Subir contenido</DialogTitle>
          <DialogContent>
            <Stack spacing={2} pt={0.5}>
              <Typography variant="body2" color="text.secondary">
                Al comenzar, el modal se cerrará y podrás seguir navegando por
                el admin. No cierres ni recargues la pestaña hasta que termine.
              </Typography>
              <NumberInput
                source="semana"
                label="Semana de embarazo"
                validate={required("Indica la semana de embarazo")}
                fullWidth
              />
              <RadioButtonGroupInput
                source="categoryVideo"
                label="Categoría de vídeo"
                choices={[
                  { id: "1", name: "Vídeo corto" },
                  { id: "2", name: "Vídeo premium" },
                  { id: "4", name: "Latido" },
                ]}
              />
              <RadioButtonGroupInput
                source="categoryImage"
                label="Categoría de imagen"
                choices={[
                  { id: "0", name: "Foto" },
                  { id: "3", name: "Imagen 8K" },
                ]}
              />
              <ImageInput
                source="images"
                label="Imágenes y vídeos"
                validate={required("Selecciona al menos un archivo")}
                options={{
                  onDropRejected: (files: FileRejection[]) => {
                    notify(
                      "Error:" +
                        files.map((file) =>
                          file.errors.map(
                            (error) => `${file.file.name}: ${error.message}`
                          )
                        ),
                      { type: "error" }
                    );
                  },
                }}
                accept="video/*,image/*"
                multiple
              >
                <PreviewImageField source="src" width={80} height={80} />
              </ImageInput>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <MuiButton onClick={() => setOpen(false)}>Cancelar</MuiButton>
            <MuiButton type="submit" variant="contained">
              Iniciar subida
            </MuiButton>
          </DialogActions>
        </Form>
      </Dialog>
    </Fragment>
  );
}

export default MediaUploadDialog;
